import React, {useEffect, useState} from 'react';
import {Trans} from '@ui/i18n/trans';
import {Button} from '@ui/buttons/button';
import {AddIcon} from '@ui/icons/material/Add';
import {Link} from 'react-router-dom';
import {Skeleton} from '@ui/skeleton/skeleton';
import {Accordion, AccordionItem} from '@ui/accordion/accordion';
import {Title} from '@app/titles/models/title';
import {LiensListDatatablePage} from '@app/liens/liens-list-datatable-page';
import {NzbListDatatablePage} from '@app/nzb/nzb-list-datatable-page';
import {useLinksFilters} from '@common/http/links-filters';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {ExpandMoreIcon} from '@ui/icons/material/ExpandMore';
import {apiClient} from '@common/http/query-client';
import {ArchiveIcon} from '@ui/icons/material/Archive';
import {LinkIcon} from '@ui/icons/material/Link';
import {HelpIcon} from '@ui/icons/material/Help';
import {
  useDownloadGetTitle,
  UseDownloadPageResponse,
} from '@app/download/requests/use-download-get-title';
import {Lien} from '@app/titles/models/lien';

interface DynamicIframeProps {
  title: Title;
  season?: number;
}

interface SeasonSelectorProps {
  selectedSeason: number;
  onSeasonChange: (season: number) => void;
  data: UseDownloadPageResponse;
}

function SeasonSelector({
  selectedSeason,
  onSeasonChange,
  data,
}: SeasonSelectorProps): JSX.Element | null {
  if (!data.title.seasons) return null;
  const seasons = data.title.seasons;
  const title = data.title;
  const selected = seasons.find(season => season.number === selectedSeason);
  if (!selected) return null;

  return (
    <MenuTrigger
      selectedValue={selectedSeason}
      onSelectionChange={newValue => onSeasonChange(newValue as number)}
      selectionMode="single"
    >
      {selected && (
        <Button
          variant="outline"
          startIcon={<ExpandMoreIcon />}
          className="mr-4"
          onClick={() => onSeasonChange(selected.number)}
        >
          <Trans message="Season :number" values={{number: selected.number}} />
          <>
            {' '}
            ({data.last_episode?.episode}/{selected.episode_count}){' '}
            {(data.last_episode?.episode === selected?.episode_count ||
              data.full_saison) && <Trans message="Complete" />}
          </>
        </Button>
      )}

      <Menu>
        {seasons.map(season => (
          <MenuItem value={season.number} key={season.number}>
            <Trans message="Season :number" values={{number: season.number}} />
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}

const cache: Record<string, FetchLinksDataResponse> = {};

interface FetchLinksDataResponse {
  liens: number;
  nzbs: number;
}

async function fetchLinksData(
  title_id: number,
  season?: number,
): Promise<FetchLinksDataResponse> {
  const cacheKey = `${title_id}-${season}`;
  if (cache[cacheKey]) {
    return cache[cacheKey];
  }
  const data = await apiClient
    .get(`count/${title_id}`, {params: {season}})
    .then(response => response.data);
  cache[cacheKey] = data;
  return data;
}

function ContentList({title, season}: {title: Title; season?: number}) {
  const {data} = useLinksFilters(title.id);

  const [totalActiveLinks, setTotalActiveLinks] = useState<number>(
    title.liens_active_count,
  );
  const [totalActiveNzb, setTotalActiveNzb] = useState<number>(
    title.nzbs_active_count,
  );

  useEffect(() => {
    if (title.seasons && title.seasons.length > 0) {
      const countSeasonLink =
        title.seasons.find(s => s.number === season)?.liens_active_count ?? 0;
      const countSeasonNzb =
        title.seasons.find(s => s.number === season)?.nzbs_active_count ?? 0;

      setTotalActiveLinks(countSeasonLink);
      setTotalActiveNzb(countSeasonNzb);
    } else {
      setTotalActiveLinks(title.liens_active_count);
      setTotalActiveNzb(title.nzbs_active_count);
    }
  }, [title, season]);

  const [expandedValues, setExpandedValues] = useState<number[]>(() => {
    const saved = localStorage.getItem('expandedAccordion');
    return saved ? JSON.parse(saved) : [0];
  });

  useEffect(() => {
    localStorage.setItem('expandedAccordion', JSON.stringify(expandedValues));
  }, [expandedValues]);

  return (
    <div>
      <Accordion
        variant={'outline'}
        expandedValues={expandedValues}
        onExpandedChange={values => setExpandedValues(values as number[])}
      >
        <AccordionItem
          label={
            <div className={'flex justify-between'}>
              <div>
                <Trans
                  message="Direct Download links (:countLinks)"
                  values={{countLinks: totalActiveLinks}}
                />
              </div>
              <div>
                <a
                  href="https://darkiworld.wiki/hc/articles/7/15/4/comment-telecharger-sur-darkiworld"
                  target={'_blank'}
                  rel="noreferrer"
                  className={'text-primary'}
                >
                  <HelpIcon className={'mr-6'} size={'xs'} />
                  <Trans message={'How to download a direct link'} />
                </a>
              </div>
            </div>
          }
          startIcon={<LinkIcon />}
        >
          {data ? (
            <LiensListDatatablePage
              season={season}
              filtersData={data}
              title={title}
            />
          ) : (
            <Skeleton />
          )}
        </AccordionItem>
        {title.nzbs_active_count && (
          <AccordionItem
            label={
              <div className={'flex justify-between'}>
                <div>
                  <Trans
                    message="Nzb (NewsGroups) (:countNzbs)"
                    values={{countNzbs: totalActiveNzb}}
                  />
                </div>
                <div>
                  <a
                    href="https://darkiworld.wiki/hc/articles/7/15/42/telecharger-un-nzb"
                    target={'_blank'}
                    className={'text-primary'}
                    rel="noreferrer"
                  >
                    <HelpIcon className={'mr-6'} size={'xs'} />
                    <Trans message={'How to download a nzb'} />
                  </a>
                </div>
              </div>
            }
            startIcon={<ArchiveIcon />}
          >
            {data ? (
              <NzbListDatatablePage
                season={season}
                filtersData={data}
                title={title}
              />
            ) : (
              <Skeleton />
            )}
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}

const DynamicIframe: React.FC<DynamicIframeProps> = ({title, season}) => {
  const [selectedSeason, setSelectedSeason] = useState<number>(season ?? 1);

  const query = useDownloadGetTitle(selectedSeason);
  const data = query.data;
  if (query.isPending || !data) return <Skeleton className="max-w-240" />;
  return (
    <div>
      <div className="mb-24 mt-48 flex justify-between">
        <div>
          {title.is_series ? (
            <SeasonSelector
              selectedSeason={selectedSeason}
              onSeasonChange={setSelectedSeason}
              data={data}
            />
          ) : null}
        </div>
        <div>
          <Link to={`/admin/liens/${title.id}/new`}>
            <Button
              startIcon={<AddIcon />}
              variant={'outline'}
              color={'primary'}
            >
              <Trans message="Add link" />
            </Button>
          </Link>
        </div>
      </div>
      <div>
        <ContentList title={title} season={selectedSeason} />
      </div>
    </div>
  );
};

export default DynamicIframe;
